
import { useEffect, useRef, useState } from 'react';

export function CalendarSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Load the external form embed script
    const existingScript = document.querySelector('script[src="https://links.dino-nuggets.com/js/form_embed.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://links.dino-nuggets.com/js/form_embed.js';
      script.type = 'text/javascript';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <section id="schedule" ref={sectionRef} className="relative py-16 lg:py-20">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAFBFC] via-white to-[#FAFBFC]"></div>

      <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Section header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-calendar-line text-primary text-sm"></i>
              <span className="text-primary text-xs font-bold tracking-wide uppercase">Book Now</span>
            </div>
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-secondary mb-3">
              Schedule Your Onboarding Session
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto leading-relaxed">
              Pick a time that works best for you. This quick call is all we need to get your 
              business automation up and running.
            </p>
          </div>

          {/* Calendar embed card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
            {/* Card header accent */}
            <div className="h-1.5 bg-gradient-to-r from-primary via-primary/80 to-primary/50"></div>
            
            <div className="p-6 lg:p-8">
              {/* Info bar */}
              <div className="flex flex-wrap items-center justify-center gap-6 mb-8 pb-6 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <i className="ri-time-line text-primary text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400 uppercase tracking-wide">Duration</p>
                    <p className="text-sm font-semibold text-secondary">30 Minutes</p>
                  </div>
                </div>
                <div className="w-px h-8 bg-gray-100 hidden sm:block"></div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <i className="ri-video-chat-line text-primary text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400 uppercase tracking-wide">Format</p>
                    <p className="text-sm font-semibold text-secondary">Phone/Video</p>
                  </div>
                </div>
                <div className="w-px h-8 bg-gray-100 hidden sm:block"></div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-primary text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400 uppercase tracking-wide">Cost</p>
                    <p className="text-sm font-semibold text-primary">Free</p>
                  </div>
                </div>
              </div>

              {/* Embedded calendar */}
              <div className="min-h-[600px]">
                <iframe
                  src="https://links.dino-nuggets.com/widget/booking/rgkWUm6iUsEtevj7YvFJ"
                  style={{ width: '100%', border: 'none', overflow: 'hidden', minHeight: '600px' }}
                  scrolling="no"
                  id="rgkWUm6iUsEtevj7YvFJ_1771517192896"
                  title="Schedule Onboarding Session"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Reassurance */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-100">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-star-line text-primary text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-secondary">No Obligation</p>
                <p className="text-xs text-gray-400">Cancel or reschedule anytime</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-100">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-star-line text-primary text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-secondary">Dedicated Expert</p>
                <p className="text-xs text-gray-400">Work with a real human, not a bot</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-100">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-flashlight-line text-primary text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-secondary">Stress-Free Setup</p>
                <p className="text-xs text-gray-400">We'll do the heavy lifting for you</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
